import React, { useMemo, useState, useEffect } from 'react';
import { Ticket, TicketStatus } from '../../types';
import { TicketCard } from '../../components/TicketCard';
import { TransferTicketModal, TransferParams } from '../../components/TransferTicketModal';
import { ManualTransferOtpError } from '../../api/client';
import { ListForSaleModal } from '../../components/ListForSaleModal';
import { GiftTicketModal } from '../../components/GiftTicketModal';
import { MultiTransferModal } from '../../components/MultiTransferModal';
import { EventTransferModal } from '../../components/EventTransferModal';
import { useSeatSession } from '../useSeatSession';
import { Toggle, Select, Input } from '../../components/base-components';
import { useTranslation } from '../../i18n/TranslationContext';
import { useToast } from '../../contexts/ToastContext';
import { trackEvent } from '../../services/analytics';
import { useTickets, useManualTransfer, useExportTickets, useDisconnectAccount, queryKeys } from '../../hooks/useApi';
import { useQueryClient } from '@tanstack/react-query';
import {
  BATCH_TRANSFER_PLATFORMS,
  BatchTransferPlatform,
  isManualOnlyTransferable,
  isTransferableOn,
  platformOf,
  accountLabelOf,
  externalEventIdOf,
} from '../../components/transferUtils';
import {
  ActionDivider,
  Badge,
  EmptyState,
  PageHeader,
  PageShell,
  SegmentedControl,
  SkeletonList,
  StatTile,
  StatTileRow,
  cx,
  CARD_SURFACE,
  FOCUS_RING,
} from '../components';
import { ChevronDownIcon, CloseIcon, SearchIcon, SlidersIcon, TrashIcon } from '../components/icons';

/**
 * v2 port of `src/pages/TicketsPage.tsx` (DESIGN_PLAN §3.1).
 *
 * The entire logic block below is copied verbatim from v1 — every state
 * variable, hook, memo, handler, `trackEvent` call and toast string. Only the
 * JSX and the `className` strings are new. Five modals stay mounted in the same
 * order, including `GiftTicketModal`, which v1 mounts with no trigger: keeping
 * the dead wire exactly as dead as it is today is the zero-change position.
 *
 * UX rework: the seven raw controls were the first thing on the page, and all
 * seven are engine-shaped ("platform = DICE", "status = active"). They now sit
 * behind a `Filters` disclosure at every breakpoint, fronted by task-shaped
 * preset views. A preset is nothing but a named tuple of the SAME six filter
 * state values — it adds no dimension, and `filteredTickets` is untouched.
 * Because the active preset is *derived* from the live filter state rather than
 * stored alongside it, the pills and the raw controls cannot disagree.
 */

// Helper to get this weekend's dates
const getThisWeekend = () => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const daysUntilSaturday = (6 - dayOfWeek + 7) % 7 || 7;
  const saturday = new Date(now);
  saturday.setDate(now.getDate() + (dayOfWeek === 0 ? -1 : daysUntilSaturday));
  saturday.setHours(0, 0, 0, 0);
  const sunday = new Date(saturday);
  sunday.setDate(saturday.getDate() + 1);
  sunday.setHours(23, 59, 59, 999);
  return {
    from: saturday.toISOString().split('T')[0],
    to: sunday.toISOString().split('T')[0],
  };
};

/**
 * The five filter values a preset owns. `searchQuery` is deliberately NOT one
 * of them: search is the always-visible control and reads as a narrowing on top
 * of whatever view you are in, so switching view must not throw away what you
 * typed. It stays fully reachable and still counts toward the active chip.
 */
interface PresetFilterState {
  platformFilter: string;
  statusFilter: 'all' | TicketStatus;
  dateFrom: string;
  dateTo: string;
  includePast: boolean;
  /**
   * The one dimension the raw controls do NOT expose. It flips the request
   * (`banned=true`) from "everything sendable" to "only what is stuck on a
   * blocked account", so it is reachable solely through its view and is
   * cleared by every other view and by `Clear filters`.
   */
  bannedOnly: boolean;
}

type PresetId = 'available' | 'weekend' | 'transferred' | 'banned';

/**
 * Task-shaped views, each expressed purely as existing filter state.
 *
 * What can I still send, what is on this weekend, what have I already sent,
 * and what is stuck on a blocked account. Everything else (past events, one
 * platform, a date window) is a hand-rolled combination under `Filters`,
 * shown as `Custom`. `state` is a thunk because `This weekend` is relative
 * to today.
 *
 * `Banned accounts` is the review view David asked for: the server withholds
 * a blocked account's tickets from every other view (they cannot be
 * transferred), so without it the user could only see a count. Here they are
 * grouped by account with a disconnect button on each group, so a ban can be
 * reviewed and the account removed without leaving the page.
 *
 * There is deliberately no `Listed` preset: "listed for sale" lives on
 * `listing.status`, which `filteredTickets` does not look at, and inventing it
 * would be a new filter dimension. The `Listed` stat tile still reports it.
 */
const PRESETS: {
  id: PresetId;
  label: string;
  hint: string;
  state: () => PresetFilterState;
}[] = [
  {
    id: 'available',
    label: 'Available to transfer',
    hint: 'Still in your hands, for events that have not happened yet.',
    state: () => ({
      platformFilter: 'all',
      statusFilter: 'active',
      dateFrom: '',
      dateTo: '',
      includePast: false,
      bannedOnly: false,
    }),
  },
  {
    id: 'weekend',
    label: 'This weekend',
    hint: 'Everything with doors on Saturday or Sunday.',
    state: () => {
      const weekend = getThisWeekend();
      return {
        platformFilter: 'all',
        statusFilter: 'all',
        dateFrom: weekend.from,
        dateTo: weekend.to,
        includePast: false,
        bannedOnly: false,
      };
    },
  },
  {
    id: 'transferred',
    label: 'Transferred',
    hint: 'Already sent out, past events included.',
    state: () => ({
      platformFilter: 'all',
      statusFilter: 'transferred',
      dateFrom: '',
      dateTo: '',
      includePast: true,
      bannedOnly: false,
    }),
  },
  {
    id: 'banned',
    label: 'Banned accounts',
    hint: 'Tickets on accounts the platform has blocked, grouped by account. Review them, then disconnect the account.',
    state: () => ({
      platformFilter: 'all',
      statusFilter: 'all',
      dateFrom: '',
      dateTo: '',
      includePast: true,
      bannedOnly: true,
    }),
  },
];

const presetStateOf = (id: PresetId): PresetFilterState =>
  (PRESETS.find((p) => p.id === id) || PRESETS[0]).state();

/** View preferences only — never ticket data, never a filter the URL can't rebuild. */
const PRESET_STORAGE_KEY = 'flashtools.v2.tickets.preset';
const VIEW_MODE_STORAGE_KEY = 'flashtools.v2.tickets.viewMode';

const readStored = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // Safari private mode / disabled storage — a remembered view is a nicety.
    return null;
  }
};

const writeStored = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* see above */
  }
};

const readStoredPreset = (): PresetId => {
  const raw = readStored(PRESET_STORAGE_KEY);
  return PRESETS.some((p) => p.id === raw) ? (raw as PresetId) : 'available';
};

const readStoredViewMode = (): 'tickets' | 'events' => {
  const raw = readStored(VIEW_MODE_STORAGE_KEY);
  return raw === 'tickets' || raw === 'events' ? raw : 'events';
};

/**
 * Buckets for the event list. Presentational grouping over the already
 * date-sorted `groupedByEvent`; the original index rides along untouched
 * because `EventTransferModal` addresses groups by it.
 *
 * The bucket reads the `YYYY-MM-DD` prefix rather than parsing the timestamp:
 * event dates arrive both venue-local and Z-suffixed, and a header only needs
 * the day. The card below still renders the exact venue-local time.
 */
const EVENT_SECTION_LABELS = {
  earlier: 'Earlier',
  today: 'Today',
  week: 'Next 7 days',
  later: 'Later',
} as const;

/** v1's header action button treatment, kept byte-for-byte on the colours. */
const HEADER_ACTION_CLASS = cx(
  'px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2',
  'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white',
  'disabled:opacity-40 disabled:cursor-not-allowed border border-white/10',
  FOCUS_RING,
);

export const TicketsScreen: React.FC = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useToast();
  // Read once, before the first render, so a returning user's preset is already
  // in `useTickets`' params and we never fire the default query then re-fire.
  const [initialFilters] = useState<PresetFilterState>(() => presetStateOf(readStoredPreset()));
  const [searchQuery, setSearchQuery] = useState('');
  const [platformFilter, setPlatformFilter] = useState<string>(initialFilters.platformFilter);
  const [statusFilter, setStatusFilter] = useState<'all' | TicketStatus>(initialFilters.statusFilter);
  const [includePast, setIncludePast] = useState(initialFilters.includePast);
  const [bannedOnly, setBannedOnly] = useState(initialFilters.bannedOnly);
  const [dateFrom, setDateFrom] = useState<string>(initialFilters.dateFrom);
  const [dateTo, setDateTo] = useState<string>(initialFilters.dateTo);
  const [viewMode, setViewMode] = useState<'tickets' | 'events'>(readStoredViewMode);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [listTicket, setListTicket] = useState<Ticket | null>(null);
  const [listModalOpen, setListModalOpen] = useState(false);
  const [giftModalOpen, setGiftModalOpen] = useState(false);
  const { isSeat } = useSeatSession();
  const [multiTransferOpen, setMultiTransferOpen] = useState(false);
  // Event-level transfer for platforms the batch endpoint can't address.
  // Holds the whole set; the modal moves them in one manual-transfer call.
  const [manualGroupTickets, setManualGroupTickets] = useState<Ticket[] | null>(null);
  const [eventTransferTarget, setEventTransferTarget] = useState<
    { groupIdx: number; platform: BatchTransferPlatform } | null
  >(null);
  // Presentational only (DESIGN_PLAN §4.4): the seven raw controls live behind
  // the `Filters` disclosure at EVERY breakpoint now that presets front them.
  // No filter value and no request depends on this.
  const [filtersOpen, setFiltersOpen] = useState(false);

  // API params - by default only returns upcoming, pass include_past=true for all
  const { data, isLoading: loading } = useTickets({
    include_past: includePast || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    banned: bannedOnly || undefined,
  });
  const tickets = useMemo(() => data?.tickets || [], [data?.tickets]);
  const queryClient = useQueryClient();
  const disconnectMutation = useDisconnectAccount();

  useEffect(() => { trackEvent('tickets_page_viewed'); }, []);

  const transferMutation = useManualTransfer();
  const exportMutation = useExportTickets();

  // Disconnecting from here is the point of the Banned accounts view. The
  // hook refetches accounts; the tickets list must follow or the group it
  // just removed would sit there until the next background refresh.
  const handleDisconnectBanned = async (accountId: number, label: string) => {
    try {
      await disconnectMutation.mutateAsync(accountId);
      await queryClient.refetchQueries({ queryKey: queryKeys.tickets });
      showSuccess(`Disconnected ${label}`);
      trackEvent('account_disconnected', { accountId: String(accountId), source: 'tickets_banned_view' });
    } catch (error: any) {
      showError(error?.message || 'Failed to disconnect account');
    }
  };

  // A blocked account cannot send, so a transfer opened from this view would
  // only fail later, inside the modal. Say so up front instead.
  const refuseBannedTransfer = () => {
    showError('This account is blocked by the platform, so it cannot transfer tickets. Disconnect it instead.');
  };

  const handleOpenTransfer = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setModalOpen(true);
    trackEvent('transfer_modal_opened', { ticketId: String(ticket.id), platform: ticket.platform || '' });
  };

  const handleOpenListForSale = (ticket: Ticket) => {
    setListTicket(ticket);
    setListModalOpen(true);
    trackEvent('list_for_sale_modal_opened', { ticketId: String(ticket.id), platform: ticket.platform || '' });
  };

  const handleTransfer = async (params: TransferParams) => {
    try {
      const res = await transferMutation.mutateAsync(params);
      // A 202 is AXS asking for a code, not a transfer. The modal shows the
      // prompt; a success toast here would say the opposite.
      if (res.otp_required) return res;
      if (res.message) {
        showSuccess(res.message);
      }
      trackEvent('ticket_transferred', { platform: params.platform, ticketCount: params.ticketIds.length });
      return res;
    } catch (error: any) {
      // A refused code is spoken for by the prompt itself, right next to the
      // input the seller has to use — a toast would only repeat it.
      if (!(error instanceof ManualTransferOtpError)) {
        showError(error?.message || 'Failed to transfer ticket');
      }
      throw error; // Re-throw so the modal can handle it
    }
  };

  const handleExportCSV = async () => {
    try {
      // Prepare export filters
      const exportFilters: {
        platform?: string;
        include_past?: boolean;
        date_from?: string;
        date_to?: string;
        status?: string;
      } = {};

      if (statusFilter !== 'all') exportFilters.status = statusFilter;

      if (platformFilter !== 'all') {
        exportFilters.platform = platformFilter;
      }
      if (includePast) {
        exportFilters.include_past = true;
      }
      if (dateFrom) {
        exportFilters.date_from = dateFrom;
      }
      if (dateTo) {
        exportFilters.date_to = dateTo;
      }

      const blob = await exportMutation.mutateAsync(exportFilters);

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `tickets_export_${timestamp}.csv`;

      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      showSuccess('Tickets exported successfully');
      trackEvent('tickets_exported', { ticketCount: filteredTickets.length, platform: platformFilter });
    } catch (error: any) {
      showError(error?.message || 'Failed to export tickets');
    }
  };

  // Client-side filtering for platform, status, and search query
  // Date filtering is handled by the API (include_past, date_from, date_to)
  const filteredTickets = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    return tickets.filter((t) => {
      const ticketPlatform = (t.external_platform_connection?.platform || t.platform || '').toLowerCase();
      let rawStatus = (t.ticket_status || t.status || 'active').toString().toLowerCase();
      if (rawStatus === 'removed' || rawStatus === 'removed_but_sold') {
        rawStatus = 'transferred';
      } else if (rawStatus === 'potentially_removed') {
        rawStatus = 'pending';
      }
      const ticketStatus = rawStatus;

      if (platformFilter !== 'all' && ticketPlatform !== platformFilter.toLowerCase()) return false;
      if (statusFilter !== 'all' && ticketStatus !== statusFilter) return false;

      if (query) {
        const eventInfo = typeof t.event === 'object' ? t.event as any : null;
        const eventName = (eventInfo?.name || t.event_name || '').toLowerCase();
        const venueName = (eventInfo?.venue?.name || t.venue_name || '').toLowerCase();
        const ticketType = ((t.ticket_metadata as any)?.type?.name || t.ticket_type || '').toLowerCase();
        const platform = ticketPlatform;

        if (
          !eventName.includes(query) &&
          !venueName.includes(query) &&
          !ticketType.includes(query) &&
          !platform.includes(query)
        ) return false;
      }

      return true;
    });
  }, [tickets, platformFilter, statusFilter, searchQuery]);

  // Compute stats
  const stats = useMemo(() => {
    const active = filteredTickets.filter(t => {
      const s = (t.ticket_status || t.status || 'active').toString().toLowerCase();
      return s === 'active';
    }).length;
    const transferred = filteredTickets.filter(t => {
      const s = (t.ticket_status || t.status || '').toString().toLowerCase();
      return s === 'removed' || s === 'removed_but_sold' || s === 'transferred';
    }).length;
    const listed = filteredTickets.filter(t => (t as any).listing?.status === 'ACTIVE').length;

    // Calculate total value
    const totalValue = filteredTickets.reduce((sum, t) => {
      const metadata = t.ticket_metadata as any;
      const cost = metadata?.cost || metadata?.type?.cost;
      let price = cost?.amount ?? t.price_paid ?? 0;
      if (price > 100) price = price / 100;
      return sum + price;
    }, 0);

    return { total: filteredTickets.length, active, transferred, listed, totalValue };
  }, [filteredTickets]);

  // Group tickets by event for event-level view
  const groupedByEvent = useMemo(() => {
    const groups: Record<string, {
      eventName: string;
      eventDate: string;
      timezone?: string;
      venue: string;
      imageUrl: string | null;
      externalEventIds: Record<string, string>;
      tickets: Ticket[];
    }> = {};
    filteredTickets.forEach((t) => {
      const eventInfo = typeof t.event === 'object' ? t.event as any : null;
      const eventName = eventInfo?.name || t.event_name || 'Unknown Event';
      const eventDate = eventInfo?.event_date_local || eventInfo?.event_date_utc || t.event_date || '';
      const timezone = eventInfo?.timezone;
      const venueName = eventInfo?.venue?.name || t.venue_name || '';
      const imageUrl = eventInfo?.image_url || null;
      const key = `${eventName}-${eventDate}`;

      if (!groups[key]) {
        groups[key] = { eventName, eventDate, timezone, venue: venueName, imageUrl, externalEventIds: {}, tickets: [] };
      }
      if (imageUrl && !groups[key].imageUrl) {
        groups[key].imageUrl = imageUrl;
      }
      // Each platform addresses the event by its own id, so keep them separately.
      const ticketPlatform = platformOf(t);
      const externalEventId = externalEventIdOf(t, ticketPlatform);
      if (ticketPlatform && externalEventId && !groups[key].externalEventIds[ticketPlatform]) {
        groups[key].externalEventIds[ticketPlatform] = externalEventId;
      }
      groups[key].tickets.push(t);
    });

    return Object.values(groups).sort((a, b) => {
      if (!a.eventDate) return 1;
      if (!b.eventDate) return -1;
      return new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime();
    });
  }, [filteredTickets]);

  // Chronological sections over the already-sorted event groups. Purely
  // presentational: same groups, same order, same indices.
  const eventSections = useMemo(() => {
    type EventGroup = (typeof groupedByEvent)[number];
    const sections: { key: keyof typeof EVENT_SECTION_LABELS; items: { group: EventGroup; idx: number }[] }[] = [
      { key: 'earlier', items: [] },
      { key: 'today', items: [] },
      { key: 'week', items: [] },
      { key: 'later', items: [] },
    ];
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    groupedByEvent.forEach((group, idx) => {
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(group.eventDate || '');
      // Undated groups already sort last, so they belong with `Later`.
      let key: keyof typeof EVENT_SECTION_LABELS = 'later';
      if (m) {
        const eventDay = new Date(+m[1], +m[2] - 1, +m[3]);
        const days = Math.round((eventDay.getTime() - startOfToday.getTime()) / 86400000);
        if (days < 0) key = 'earlier';
        else if (days === 0) key = 'today';
        else if (days <= 7) key = 'week';
      }
      sections.find((s) => s.key === key)!.items.push({ group, idx });
    });

    return sections.filter((s) => s.items.length > 0);
  }, [groupedByEvent]);

  const platforms = useMemo(() => {
    const platformSet = new Set(
      tickets.map((t) => (t.external_platform_connection?.platform || t.platform || '').toUpperCase())
    );
    return Array.from(platformSet).filter(Boolean);
  }, [tickets]);

  const handleThisWeekend = () => {
    const weekend = getThisWeekend();
    setDateFrom(weekend.from);
    setDateTo(weekend.to);
    trackEvent('tickets_filter_this_weekend');
  };

  const clearDateFilter = () => {
    setDateFrom('');
    setDateTo('');
  };

  // The active preset is derived, never stored: whatever the raw controls say
  // right now decides which pill is lit. Editing a control by hand therefore
  // cannot leave a pill claiming something untrue — it falls to `Custom`.
  const activePreset = useMemo<PresetId | 'custom'>(() => {
    const match = PRESETS.find((preset) => {
      const s = preset.state();
      return (
        s.platformFilter === platformFilter &&
        s.statusFilter === statusFilter &&
        s.dateFrom === dateFrom &&
        s.dateTo === dateTo &&
        s.includePast === includePast &&
        s.bannedOnly === bannedOnly
      );
    });
    return match ? match.id : 'custom';
  }, [platformFilter, statusFilter, dateFrom, dateTo, includePast, bannedOnly]);

  const activePresetHint =
    activePreset === 'custom'
      ? 'Custom view — your own combination of filters.'
      : PRESETS.find((p) => p.id === activePreset)!.hint;

  // A preset only ever writes the five values it owns. `searchQuery` survives.
  const applyPreset = (id: PresetId) => {
    const next = presetStateOf(id);
    setPlatformFilter(next.platformFilter);
    setStatusFilter(next.statusFilter);
    setDateFrom(next.dateFrom);
    setDateTo(next.dateTo);
    setIncludePast(next.includePast);
    setBannedOnly(next.bannedOnly);
    trackEvent('tickets_preset_selected', { preset: id });
  };

  // Remember the last *named* view. A hand-rolled `Custom` combination is
  // transient by design — restoring five arbitrary values (two of them dates
  // that may already be in the past) would land people somewhere they can't
  // explain, so we leave the previous preset as the thing to come back to.
  useEffect(() => {
    if (activePreset !== 'custom') writeStored(PRESET_STORAGE_KEY, activePreset);
  }, [activePreset]);

  useEffect(() => {
    writeStored(VIEW_MODE_STORAGE_KEY, viewMode);
  }, [viewMode]);

  // Render the event date in venue-local time. Mirrors TicketCard.formatDate:
  // sniff for a trailing Z / +HH:MM suffix and route UTC strings through the
  // event's IANA timezone; treat unsuffixed strings as already venue-local
  // and render with timeZone: 'UTC' so the wall clock matches the venue.
  // We can't trust the field name alone — some platforms populate
  // `event_date_local` with a Z-suffixed UTC value, so the suffix wins.
  const formatEventDate = (group: { eventDate: string; timezone?: string }) => {
    if (!group.eventDate) return '';
    const opts: Intl.DateTimeFormatOptions = {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    };
    try {
      const isUtcInput = /(?:Z|[+-]\d{2}:?\d{2})$/.test(group.eventDate);
      if (isUtcInput) {
        const dt = new Date(group.eventDate);
        if (isNaN(dt.getTime())) return group.eventDate;
        return dt.toLocaleDateString('en-US', { ...opts, timeZone: group.timezone || 'UTC' });
      }
      const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(group.eventDate);
      if (!m) return group.eventDate;
      const [, y, mo, d, h = '0', mi = '0', s = '0'] = m;
      const dt = new Date(Date.UTC(+y, +mo - 1, +d, +h, +mi, +s));
      return dt.toLocaleDateString('en-US', { ...opts, timeZone: 'UTC' });
    } catch {
      return group.eventDate;
    }
  };

  const hasActiveFilters = searchQuery || platformFilter !== 'all' || statusFilter !== 'all' || includePast || dateFrom || dateTo || bannedOnly;

  const activeFilterCount = [
    searchQuery,
    platformFilter !== 'all',
    statusFilter !== 'all',
    includePast,
    dateFrom,
    dateTo,
    bannedOnly,
  ].filter(Boolean).length;

  // The Banned accounts view groups by the account, not the event: the
  // question is "which account do I remove", and every ticket under it goes
  // with it. Same filtered list, so search and platform still narrow it.
  const bannedGroups = useMemo(() => {
    if (!bannedOnly) return [];
    const groups: Record<string, {
      accountId: number | null;
      platform: string;
      label: string;
      tickets: Ticket[];
    }> = {};
    filteredTickets.forEach((t) => {
      const conn = t.external_platform_connection;
      const key = conn ? String(conn.id) : 'unknown';
      if (!groups[key]) {
        groups[key] = {
          accountId: conn?.id ?? null,
          platform: (conn?.platform || t.platform || '').toUpperCase(),
          label: accountLabelOf(t),
          tickets: [],
        };
      }
      groups[key].tickets.push(t);
    });
    return Object.values(groups).sort((a, b) => b.tickets.length - a.tickets.length);
  }, [bannedOnly, filteredTickets]);

  const hiddenBanned = data?.hidden?.banned_tickets ?? 0;
  const hiddenAccounts = data?.hidden?.banned_accounts ?? 0;

  // `Showing X of Y tickets across Z events` — PageHeader's `meta` slot at
  // `lg`+, directly above the list below it (DESIGN_PLAN §3.1).
  const resultsLine = (
    <p className="text-sm text-white/40">
      Showing <span className="font-medium text-white/70 tabular-nums">{filteredTickets.length}</span> of{' '}
      <span className="tabular-nums">{tickets.length}</span> tickets
      {viewMode === 'events' && ` across ${groupedByEvent.length} events`}
      {/* A blocked account's tickets are withheld because they cannot be
          transferred. Saying so is the point: a total that quietly drops by
          forty reads as data loss. */}
      {hiddenBanned > 0 && !bannedOnly && (
        <>
          {' · '}
          <button
            type="button"
            onClick={() => applyPreset('banned')}
            className={cx('text-amber-400/80 underline-offset-2 hover:underline', FOCUS_RING)}
            title="Review tickets on blocked accounts"
          >
            {hiddenBanned} hidden from {hiddenAccounts}{' '}
            blocked {hiddenAccounts === 1 ? 'account' : 'accounts'}
          </button>
        </>
      )}
    </p>
  );
  const resultsMeta = !loading ? <div className="hidden lg:block">{resultsLine}</div> : undefined;

  return (
    <PageShell width="wide">
      <PageHeader
        title="Inventory"
        subtitle="Manage your ticket inventory across platforms"
        meta={resultsMeta}
        actions={
          <>
            {/* Transfer Multiple Tickets Button */}
            <button
              onClick={() => { setMultiTransferOpen(true); trackEvent('multi_transfer_modal_opened'); }}
              disabled={loading || filteredTickets.length === 0 || bannedOnly}
              title={hasActiveFilters ? `Transfer from ${filteredTickets.length} filtered ticket${filteredTickets.length === 1 ? '' : 's'}` : 'Transfer multiple tickets'}
              className={HEADER_ACTION_CLASS}
            >
              <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
              Transfer Multiple{hasActiveFilters ? ` (${filteredTickets.length})` : ''}
            </button>

            {/* Export Button */}
            <button
              onClick={handleExportCSV}
              disabled={exportMutation.isPending || loading || tickets.length === 0}
              className={HEADER_ACTION_CLASS}
            >
              {exportMutation.isPending ? (
                <>
                  <svg className="w-4 h-4 flex-shrink-0 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Exporting...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export CSV
                </>
              )}
            </button>

            {/* View switch — a view change, not an action, so it is visually
                separated and sits at the far right of the actions row. */}
            <ActionDivider />

            <SegmentedControl<'tickets' | 'events'>
              label="Inventory view"
              tourId="tickets-view"
              value={viewMode}
              stretch
              className="lg:w-auto"
              onChange={(mode) => { setViewMode(mode); trackEvent('tickets_view_mode_changed', { mode }); }}
              options={[
                {
                  value: 'tickets',
                  label: (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      </svg>
                      Tickets
                    </span>
                  ),
                },
                {
                  value: 'events',
                  label: (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                      Events
                    </span>
                  ),
                },
              ]}
            />
          </>
        }
      />

      <div className="space-y-6">
        {/* Stats Overview */}
        {!loading && (
          <StatTileRow cols={5}>
            <StatTile label="Total" value={stats.total} subtitle="matching filters" />
            <StatTile label="Active" value={stats.active} color="green" subtitle="available" />
            <StatTile label="Transferred" value={stats.transferred} color="blue" subtitle="sent out" />
            <StatTile label="Listed" value={stats.listed} color="amber" subtitle="for sale" />
            <StatTile
              label="Value"
              value={`$${stats.totalValue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
              subtitle="inventory value"
            />
          </StatTileRow>
        )}

        {/* Views — the primary control. Each pill is a named tuple of the same
            filter state the disclosure below exposes one dimension at a time. */}
        <div className={cx(CARD_SURFACE, 'p-4 sm:p-5')}>
          <div
            role="group"
            aria-label="Ticket views"
            className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {PRESETS.map((preset) => {
              const active = preset.id === activePreset;
              return (
                <button
                  key={preset.id}
                  type="button"
                  aria-pressed={active}
                  onClick={() => applyPreset(preset.id)}
                  className={cx(
                    'min-h-[36px] flex-shrink-0 whitespace-nowrap rounded-full border px-4 py-1.5',
                    'text-sm font-medium transition-all',
                    active
                      ? 'border-[#3b82f6]/40 bg-[#3b82f6]/15 text-[#93c5fd]'
                      : 'border-white/10 bg-white/[0.03] text-white/60 hover:bg-white/[0.07] hover:text-white/80',
                    FOCUS_RING,
                  )}
                >
                  {preset.label}
                </button>
              );
            })}
            {activePreset === 'custom' && (
              <span
                className={cx(
                  'min-h-[36px] flex-shrink-0 whitespace-nowrap rounded-full border px-4 py-1.5 text-sm font-medium',
                  'border-[#3b82f6]/40 bg-[#3b82f6]/15 text-[#93c5fd]',
                  'flex items-center',
                )}
              >
                Custom
              </span>
            )}
          </div>
          {/* The hint is always on screen, never a tooltip — the pill label
              alone doesn't say which tickets a view actually contains. */}
          <p className="mt-2.5 text-xs text-white/35">{activePresetHint}</p>

          <div className="relative mt-4" data-tour="tickets-search">
            <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" />
            <input
              type="text"
              aria-label="Search tickets"
              placeholder="Search by event, venue, ticket type, or platform..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={cx(
                'w-full rounded-xl border border-white/[0.08] bg-white/[0.03] py-3 pl-11 pr-11 text-sm text-white',
                'placeholder-white/30 transition-colors focus:border-white/20 focus:outline-none',
                FOCUS_RING,
              )}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
                title="Clear search"
                className={cx(
                  'absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-white/30',
                  'transition-colors hover:text-white/60',
                  FOCUS_RING,
                )}
              >
                <CloseIcon className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="mt-4 flex items-center justify-between gap-3 border-t border-white/[0.06] pt-4">
            <button
              type="button"
              onClick={() => setFiltersOpen((open) => !open)}
              aria-expanded={filtersOpen}
              aria-controls="tickets-filter-controls"
              className={cx(
                'flex min-h-[36px] items-center gap-3 rounded-lg text-left',
                FOCUS_RING,
              )}
            >
              <SlidersIcon className="h-4 w-4 flex-shrink-0 text-white/40" />
              <span className="text-sm font-medium text-white/70">Filters</span>
              {hasActiveFilters && (
                <span className="rounded-full bg-blue-500/20 px-2 py-0.5 text-xs text-blue-400">
                  {activeFilterCount} active
                </span>
              )}
              <ChevronDownIcon
                className={cx(
                  'h-4 w-4 flex-shrink-0 text-white/40 transition-transform',
                  filtersOpen && 'rotate-180',
                )}
              />
            </button>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  setPlatformFilter('all');
                  setStatusFilter('all');
                  setIncludePast(false);
                  setBannedOnly(false);
                  clearDateFilter();
                }}
                className={cx(
                  'flex-shrink-0 rounded-md px-1 py-1 text-xs text-white/40 transition-colors hover:text-white/60',
                  FOCUS_RING,
                )}
              >
                Clear all
              </button>
            )}
          </div>

          <div
            id="tickets-filter-controls"
            className={cx('mt-4 space-y-4', filtersOpen ? 'block' : 'hidden')}
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
              <Select
                label="Platform"
                options={[
                  { value: 'all', label: 'All Platforms' },
                  ...platforms.map((p) => ({ value: p, label: p })),
                ]}
                value={platformFilter}
                onChange={(e) => setPlatformFilter(e.target.value)}
              />
              <Select
                label="Status"
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'active', label: 'Active' },
                  { value: 'transferred', label: 'Transferred' },
                  { value: 'received', label: 'Received' },
                  { value: 'cancelled', label: 'Cancelled' },
                  { value: 'delivery_pending', label: 'Delivery pending' },
                ]}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as TicketStatus | 'all')}
              />
              <Input
                type="date"
                label="From"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
              <Input
                type="date"
                label="To"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-3 border-t border-white/[0.06] pt-4 sm:flex-row sm:items-center sm:justify-between">
              <Toggle
                checked={includePast}
                label="Include past events"
                onChange={(checked: boolean) => setIncludePast(checked)}
              />
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={handleThisWeekend}
                  className={cx(
                    'rounded-md bg-white/5 px-3 py-1.5 text-xs text-white/60 transition-all',
                    'hover:bg-white/10 hover:text-white/80',
                    FOCUS_RING,
                  )}
                >
                  This Weekend
                </button>
                {(dateFrom || dateTo) && (
                  <button
                    type="button"
                    onClick={clearDateFilter}
                    className={cx(
                      'rounded-md px-3 py-1.5 text-xs text-white/40 transition-colors hover:text-white/60',
                      FOCUS_RING,
                    )}
                  >
                    Clear dates
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Results Count */}
        {!loading && <div className="lg:hidden">{resultsLine}</div>}

        {/* Content */}
        {loading ? (
          <div className="space-y-4">
            <p role="status" className="text-sm text-white/40">Loading inventory...</p>
            <SkeletonList count={4} as="row" />
          </div>
        ) : bannedOnly ? (
          <div className="space-y-6" data-testid="banned-accounts-view">
            {bannedGroups.map((group) => (
              <section
                key={group.accountId ?? 'unknown'}
                aria-label={`Blocked account ${group.label}`}
                className={cx(CARD_SURFACE, 'overflow-hidden')}
              >
                <div className="flex flex-wrap items-center gap-3 border-b border-white/[0.06] px-4 py-3 sm:px-5">
                  <Badge tone="red" dot>Account banned</Badge>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-white">{group.label}</p>
                    <p className="text-xs text-white/40">
                      {group.platform && `${group.platform} · `}
                      <span className="tabular-nums">{group.tickets.length}</span>{' '}
                      {group.tickets.length === 1 ? 'ticket' : 'tickets'} still attached
                    </p>
                  </div>
                  {!isSeat && group.accountId !== null && (
                    <button
                      type="button"
                      onClick={() => handleDisconnectBanned(group.accountId as number, group.label)}
                      disabled={disconnectMutation.isPending}
                      className={cx(
                        'flex min-h-[36px] items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-1.5',
                        'text-sm font-medium text-red-300 transition-colors hover:bg-red-500/20',
                        'disabled:cursor-not-allowed disabled:opacity-40',
                        FOCUS_RING,
                      )}
                    >
                      <TrashIcon className="h-4 w-4" />
                      Disconnect account
                    </button>
                  )}
                </div>
                <div className="space-y-3 p-4 sm:p-5">
                  {group.tickets.map((ticket) => (
                    <TicketCard key={ticket.id} ticket={ticket} onTransfer={refuseBannedTransfer} />
                  ))}
                </div>
              </section>
            ))}
            {!bannedGroups.length && (
              <EmptyState
                title="No tickets on blocked accounts"
                body="Every connected account is in good standing, or the blocked ones have already been disconnected."
                icon={
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                }
              />
            )}
          </div>
        ) : viewMode === 'events' ? (
          <div className="space-y-6">
            {eventSections.map((section) => (
              <section key={section.key} className="space-y-3">
                {/* One bucket means the header is noise, so it is dropped. */}
                {eventSections.length > 1 && (
                  <div className="flex items-center gap-3">
                    <h2 className="text-xs font-medium uppercase tracking-wider text-white/40">
                      {EVENT_SECTION_LABELS[section.key]}
                    </h2>
                    <span className="text-xs tabular-nums text-white/25">{section.items.length}</span>
                    <div className="h-px flex-1 bg-white/[0.06]" />
                  </div>
                )}
                {section.items.map(({ group, idx }) => {
                  // A batch transfer carries one platform, so each platform with
                  // transferable tickets (and a known event id) gets its own button.
                  const transferTargets = BATCH_TRANSFER_PLATFORMS
                    .map((p) => ({
                      platform: p,
                      tickets: group.tickets.filter((t) => isTransferableOn(t, p)),
                      eventId: group.externalEventIds[p] || '',
                    }))
                    .filter((x) => x.tickets.length > 0 && !!x.eventId);

                  // Platforms the batch endpoint cannot carry — AXS above all.
                  // They used to produce no button here at all, so an AXS-only
                  // event looked untransferable unless you found the Tickets
                  // view. They go through the per-ticket modal instead, which
                  // takes every id in one call. Grouped by platform because one
                  // transfer carries one platform.
                  const manualTargets = Object.entries(
                    group.tickets.filter(isManualOnlyTransferable).reduce((acc, t) => {
                      const p = platformOf(t);
                      (acc[p] ||= []).push(t);
                      return acc;
                    }, {} as Record<string, Ticket[]>),
                  ).sort(([a], [b]) => a.localeCompare(b));

                  // Which account(s) hold this event's tickets. Every ticket in
                  // the group counts, not just the sendable ones: "which phone
                  // number are my Adriatique tickets on" (FT-12) is asked about
                  // the inventory as a whole, and an event on a single account
                  // still needs its account named.
                  const accountSpread: { label: string; count: number }[] = (() => {
                    const map = new Map<string, { label: string; count: number }>();
                    group.tickets.forEach((t) => {
                      const conn = t.external_platform_connection;
                      const key = conn ? `c${conn.id}` : 'none';
                      if (!map.has(key)) map.set(key, { label: accountLabelOf(t, true), count: 0 });
                      map.get(key)!.count++;
                    });
                    return Array.from(map.values()).sort((a, b) => b.count - a.count);
                  })();

                  // One button per platform, never collapsed into one — each
                  // carries a different externalEventId (DICE and TAO both batch).
                  const renderTransferButton = (
                    target: (typeof transferTargets)[number],
                    className?: string,
                  ) => (
                    <button
                      key={target.platform}
                      type="button"
                      onClick={() => {
                        setEventTransferTarget({ groupIdx: idx, platform: target.platform });
                        trackEvent('event_transfer_modal_opened', {
                          eventName: group.eventName,
                          platform: target.platform,
                          transferableCount: target.tickets.length,
                        });
                      }}
                      className={cx(
                        'rounded-lg border border-blue-500/30 bg-blue-500/15 px-3 py-1.5 text-xs font-medium text-blue-300',
                        'transition-all hover:bg-blue-500/25 hover:text-blue-200',
                        FOCUS_RING,
                        className,
                      )}
                    >
                      {transferTargets.length + manualTargets.length > 1
                        ? `Transfer ${target.platform} (${target.tickets.length})`
                        : `Transfer (${target.tickets.length})`}
                    </button>
                  );

                  // Same button, same styling — the only difference is which
                  // endpoint ends up carrying it, which is not the seller's
                  // problem.
                  const renderManualTransferButton = (
                    platform: string,
                    tickets: Ticket[],
                    className?: string,
                  ) => (
                    <button
                      key={`manual-${platform}`}
                      type="button"
                      onClick={() => {
                        setManualGroupTickets(tickets);
                        trackEvent('event_transfer_modal_opened', {
                          eventName: group.eventName,
                          platform,
                          transferableCount: tickets.length,
                          via: 'manual',
                        });
                      }}
                      className={cx(
                        'rounded-lg border border-blue-500/30 bg-blue-500/15 px-3 py-1.5 text-xs font-medium text-blue-300',
                        'transition-all hover:bg-blue-500/25 hover:text-blue-200',
                        FOCUS_RING,
                        className,
                      )}
                    >
                      {transferTargets.length + manualTargets.length > 1
                        ? `Transfer ${platform} (${tickets.length})`
                        : `Transfer (${tickets.length})`}
                    </button>
                  );

                  // Group tickets by type and status
                  const typeGroups: Record<string, { active: number; transferred: number; pending: number; other: number }> = {};
                  group.tickets.forEach((ticket) => {
                    const metadata = ticket.ticket_metadata as any;
                    const ticketType = metadata?.type?.name || ticket.ticket_type || 'GA';
                    const rawStatus = (ticket.ticket_status || ticket.status || 'active').toString().toLowerCase();
                    const status = rawStatus === 'removed' || rawStatus === 'removed_but_sold' ? 'transferred' : rawStatus;

                    if (!typeGroups[ticketType]) {
                      typeGroups[ticketType] = { active: 0, transferred: 0, pending: 0, other: 0 };
                    }
                    if (status === 'active') typeGroups[ticketType].active++;
                    else if (status === 'transferred') typeGroups[ticketType].transferred++;
                    else if (status === 'delivery_pending') typeGroups[ticketType].pending++;
                    else typeGroups[ticketType].other++;
                  });

                  return (
                    <article
                      key={idx}
                      className={cx(
                        CARD_SURFACE,
                        'p-4 transition-all hover:border-white/[0.08] hover:bg-white/[0.03] sm:p-5',
                      )}
                    >
                      <div className="flex gap-3 sm:gap-4">
                        {group.imageUrl && (
                          <img
                            src={group.imageUrl}
                            alt={group.eventName}
                            className="h-12 w-12 flex-shrink-0 rounded-xl object-cover md:h-16 md:w-16"
                          />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between md:gap-4">
                            <div className="min-w-0">
                              <h3 className="truncate text-base font-medium text-white">{group.eventName}</h3>
                              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                {group.eventDate && (
                                  <span className="text-sm text-white/40">{formatEventDate(group)}</span>
                                )}
                                {group.venue && (
                                  <>
                                    <span className="text-white/20">·</span>
                                    <span className="text-sm text-white/40">{group.venue}</span>
                                  </>
                                )}
                              </div>
                              {accountSpread.length > 0 && (
                                <div
                                  className="mt-1.5 flex flex-wrap items-center gap-1.5"
                                  data-testid="event-accounts"
                                >
                                  {accountSpread.map((a) => (
                                    <Badge
                                      key={a.label}
                                      size="sm"
                                      mono
                                      title={`${a.count} ticket${a.count === 1 ? '' : 's'} in the ${a.label} account`}
                                    >
                                      {accountSpread.length > 1 ? `${a.count} in ${a.label}` : a.label}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                              {/* Count moves under the title below md */}
                              <p className="mt-1.5 text-sm text-white/40 md:hidden">
                                <span className="tabular-nums">{group.tickets.length}</span> tickets
                              </p>
                            </div>

                            <div className="hidden flex-shrink-0 items-start gap-3 md:flex">
                              {transferTargets.map((target) => renderTransferButton(target))}
                              {manualTargets.map(([p, ts]) => renderManualTransferButton(p, ts))}
                              <div className="min-w-[64px] text-right">
                                <div className="text-2xl font-semibold tabular-nums text-white">{group.tickets.length}</div>
                                <div className="text-xs text-white/40">tickets</div>
                              </div>
                            </div>
                          </div>

                          <div className="mt-3 flex flex-wrap gap-1.5">
                            {Object.entries(typeGroups).map(([type, counts]) => (
                              <div key={type} className="flex items-center gap-1">
                                {counts.active > 0 && (
                                  <Badge tone="green">{counts.active}x {type}</Badge>
                                )}
                                {counts.transferred > 0 && (
                                  <Badge tone="blue">
                                    {counts.transferred}x {type} <span className="opacity-60">(transferred)</span>
                                  </Badge>
                                )}
                                {counts.pending > 0 && (
                                  <Badge tone="amber">
                                    {counts.pending}x {type} <span className="opacity-60">(delivery pending)</span>
                                  </Badge>
                                )}
                                {counts.other > 0 && (
                                  <Badge>{counts.other}x {type}</Badge>
                                )}
                              </div>
                            ))}
                          </div>

                          {/* Below md the transfer buttons get their own full-width
                              row so they stay reachable on touch. */}
                          {transferTargets.length + manualTargets.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-2 md:hidden">
                              {transferTargets.map((target) =>
                                renderTransferButton(target, 'min-h-[40px] min-w-[140px] flex-1'),
                              )}
                              {manualTargets.map(([p, ts]) =>
                                renderManualTransferButton(p, ts, 'min-h-[40px] min-w-[140px] flex-1'),
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </section>
            ))}
            {!groupedByEvent.length && (
              <EmptyState
                title="No tickets match your filters"
                icon={
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                  </svg>
                }
              />
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredTickets.map((ticket) => (
              <TicketCard
                key={ticket.id}
                ticket={ticket}
                onTransfer={handleOpenTransfer}
                // A team member sends; they do not sell. Creating a listing is the
                // owner's act — the endpoint refuses a team member, and TicketCard
                // hides the button when the handler is absent, so this omission
                // IS the hiding.
                onListForSale={isSeat ? undefined : handleOpenListForSale}
              />
            ))}
            {!filteredTickets.length && (
              <EmptyState
                title="No tickets match your filters"
                icon={
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                  </svg>
                }
              />
            )}
          </div>
        )}
      </div>

      <TransferTicketModal
        ticket={selectedTicket}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onTransfer={handleTransfer}
      />

      {/* Event-level transfer for a platform the batch endpoint can't address.
          Deliberately the same modal as the per-ticket flow — same fields, same
          validation (AXS asks for the recipient's name), same endpoint — just
          carrying every ticket in the group instead of one. */}
      <TransferTicketModal
        ticket={manualGroupTickets?.[0] ?? null}
        alsoTransfer={manualGroupTickets?.slice(1)}
        open={manualGroupTickets !== null}
        onClose={() => setManualGroupTickets(null)}
        onTransfer={handleTransfer}
      />

      <ListForSaleModal
        ticket={listTicket}
        open={listModalOpen}
        onClose={() => setListModalOpen(false)}
      />

      {/* Mounted with no trigger, exactly as in v1 (feature map §4,
          "Unreachable implementation"). Adding a trigger would be a new
          feature; removing the modal would lose one. */}
      <GiftTicketModal
        tickets={filteredTickets}
        open={giftModalOpen}
        onClose={() => setGiftModalOpen(false)}
      />

      <MultiTransferModal
        open={multiTransferOpen}
        onClose={() => setMultiTransferOpen(false)}
        tickets={filteredTickets}
      />

      {eventTransferTarget !== null && groupedByEvent[eventTransferTarget.groupIdx] && (
        <EventTransferModal
          open={true}
          onClose={() => setEventTransferTarget(null)}
          eventName={groupedByEvent[eventTransferTarget.groupIdx].eventName}
          eventDateLabel={formatEventDate(groupedByEvent[eventTransferTarget.groupIdx])}
          venueLabel={groupedByEvent[eventTransferTarget.groupIdx].venue}
          externalEventId={
            groupedByEvent[eventTransferTarget.groupIdx].externalEventIds[eventTransferTarget.platform] || ''
          }
          tickets={groupedByEvent[eventTransferTarget.groupIdx].tickets}
          platform={eventTransferTarget.platform}
        />
      )}
    </PageShell>
  );
};
